import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Link,
  Breadcrumbs,
  Stack
} from '@mui/material';

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Home as HomeIcon, BookmarkOutlined as TocIcon } from '@mui/icons-material';

// Service documentation components
import SubmissionOverview from '../components/docs/submission/SubmissionOverview';
import SubmitDdsEndpoint from '../components/docs/submission/SubmitDdsEndpoint';
import DomesticSubmission from '../components/docs/submission/DomesticSubmission';
import ImportSubmission from '../components/docs/submission/ImportSubmission';
import TradeSubmission from '../components/docs/submission/TradeSubmission';
import AmendDdsEndpoint from '../components/docs/submission/AmendDdsEndpoint';
import RetractDdsEndpoint from '../components/docs/submission/RetractDdsEndpoint';
import RetrievalOverview from '../components/docs/retrieval/RetrievalOverview';
import GetDdsByUuids from '../components/docs/retrieval/GetDdsByUuids';
import GetDdsByReference from '../components/docs/retrieval/GetDdsByReference';
import GetStatementByIdentifiers from '../components/docs/retrieval/GetStatementByIdentifiers';
import EchoService from '../components/docs/echo/EchoService';
import Authentication from '../components/docs/Authentication';
import HSCodesEndpoint from '../components/docs/hscodes/HSCodesEndpoint';

// TOC data structure
const docsSections = {
  'authentication': {
    title: 'EUDR API Authentication',
    description: 'Authentication details for the EUDR API.',
    subsections: {
      'auth': { title: 'Authentication', component: Authentication }
    }
  },
  'echo': {
    title: 'EUDR Echo',
    description: 'The Echo Service provides a simple way to test connectivity with the EUDR API.',
    subsections: {
      'echo': { title: 'Echo Request', component: EchoService }
    }
  },
  'submission': {
    title: 'DDS Submission',
    description: 'The Submission Service allows you to create DDS (Due Diligence Statement) statements.',
    subsections: {
      'overview': { title: 'DDS Submission Overview', component: SubmissionOverview },
      'submit': { title: 'Submit DDS Base Request', component: SubmitDdsEndpoint },
      'domestic': { title: 'Domestic Submission', component: DomesticSubmission },
      'import': { title: 'Import/Export Submission', component: ImportSubmission },
      'trade': { title: 'Trade Submission', component: TradeSubmission }
    }
  },
  'retrieval': {
    title: 'DDS Retrieval',
    description: 'The Retrieval Service allows you to fetch information about existing DDS statements using various identifiers.',
    subsections: {
      'overview': { title: 'DDS Retrieval Overview', component: RetrievalOverview },
      'by-uuids': { title: 'Get DDS Info by UUIDs', component: GetDdsByUuids },
      'by-reference': { title: 'Get DDS Info by Reference', component: GetDdsByReference },
      'by-identifiers': { title: 'Get Statement by Identifiers', component: GetStatementByIdentifiers }
    }
  },
  'amend': {
    title: 'DDS Amend',
    description: 'The Amend Service allows you to modify existing DDS statements.',
    subsections: {
      'overview': { title: 'DDS Amend Overview', component: AmendDdsEndpoint }
    }
  },
  'retract': {
    title: 'DDS Retract',
    description: 'The Retract Service allows you to withdraw DDS statements from the EUDR system.',
    subsections: {
      'overview': { title: 'DDS Retract Overview', component: RetractDdsEndpoint }
    }
  },
  'hscodes': {
    title: 'HS Codes',
    description: 'The HS Codes Service allows you to search for HS Codes by section.',
    subsections: {
      'overview': { title: 'HS Codes Overview', component: HSCodesEndpoint }
    }
  }
};

export default function Docs() {
  const [section, setSection] = useState('authentication');
  const [subsection, setSubsection] = useState('auth');

  // Generate breadcrumb navigation path
  function getBreadcrumbs() {
    const paths = [
      { name: 'Home', path: '/' },
      { name: 'API Documentation', path: '/docs' },
      { name: docsSections[section].title, path: `/docs#${section}` },
    ];

    if (subsection !== 'overview' && docsSections[section].subsections[subsection]) {
      paths.push({
        name: docsSections[section].subsections[subsection].title,
        path: `/docs#${section}/${subsection}`
      });
    }

    return paths;
  }

  // Handle URL hash changes
  useEffect(() => {
    function handleHashChange() {
      const hash = window.location.hash.substring(1); // Remove the # character
      if (hash) {
        const parts = hash.split('/');
        if (parts[0] && docsSections[parts[0]]) {
          setSection(parts[0]);

          if (parts[1] && docsSections[parts[0]].subsections[parts[1]]) {
            setSubsection(parts[1]);
          } else {
            // Default to first available subsection
            const firstSubsection = Object.keys(docsSections[parts[0]].subsections)[0];
            setSubsection(firstSubsection);
          }
        }
      }
    }

    // Set initial values based on URL
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Update URL when section/subsection changes
  useEffect(() => {
    window.location.hash = `${section}/${subsection}`;
  }, [section, subsection]);

  // Handle navigation
  function handleSectionChange(newSection) {
    setSection(newSection);
    // Default to first available subsection when changing sections
    const firstSubsection = Object.keys(docsSections[newSection].subsections)[0];
    setSubsection(firstSubsection);
  }

  function handleSubsectionChange(newSubsection) {
    setSubsection(newSubsection);
  }

  // Get current component to render, with fallback for safety
  const currentSubsection = docsSections[section]?.subsections[subsection] ||
    docsSections[section]?.subsections[Object.keys(docsSections[section]?.subsections || {})[0]];
  const CurrentComponent = currentSubsection?.component || (() => <div>Documentation not found</div>);

  return (
    <Container maxWidth="lg" sx={{ flex: 1 }}>
      <Box className="inner-content" sx={{ py: 5, textAlign: 'center' }}>
        <Typography variant="h2" gutterBottom sx={{
          fontWeight: 'bold',
          fontSize: { xs: '2rem', md: '3rem' }
          
        }}>
          EUDR REST API Documentation
        </Typography>

        <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 2, maxWidth: 700, mx: "auto" }}
          >
          Comprehensive documentation for easy integration of the EUDR API into your applications.
        </Typography>

        {/* Swagger UI Link Section */}
        <Paper elevation={0} sx={{ mb: 3, p: 2.5, backgroundColor: 'background.paper', display: 'flex', alignItems: 'center', borderLeft: 4, borderLeftColor: 'primary.main', width: '100%', alignSelf: 'stretch', borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
            <span role="img" aria-label="API">🔗</span>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.08rem', color: 'text.primary' }}>
            For interactive API exploration and live testing, visit our{' '}
            <Link href="/api-docs" target="_blank" rel="noopener" underline="hover" sx={{ fontWeight: 'bold', color: 'primary.main' }}>Swagger UI</Link>.
            <br />
            <span style={{ fontWeight: 400, fontSize: '1rem', color: 'inherit' }}>
               The Swagger UI provides a user-friendly interface to try out all endpoints and view detailed request/response schemas.
            </span>
          </Typography>
        </Paper>

        {/* Breadcrumbs Navigation */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0 }}>
          {getBreadcrumbs().map((crumb, index) => (
            <Link
              key={index}
              underline="hover"
              color={index === getBreadcrumbs().length - 1 ? "text.primary" : "inherit"}
              href={crumb.path}
            >
              {index === 0 ? <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} /> : null}
              {crumb.name}
            </Link>
          ))}
        </Breadcrumbs>

        {/* Main content layout using Stack */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          sx={{
            alignItems: { xs: "stretch", md: "flex-start" },
            justifyContent: "space-between"
          }}
        >
          {/* Left Sidebar - Table of Contents */}
          <Paper
            elevation={2}
            sx={{
              width: { xs: '100%', md: '300px' },
              flexShrink: 0,
              position: { md: 'sticky' },
              top: { md: '20px' },
              maxHeight: { md: 'calc(100vh - 40px)' },
              overflow: { md: 'auto' },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ pl:2, pt: 2, borderTopLeftRadius: 8, borderTopRightRadius: 8  }}>
              <Typography sx={{ color: 'primary.secondary'}} fontWeight="bold">
                <TocIcon sx={{ mr: 0.5, fontSize: 16 }} /> API ToC
              </Typography>
            </Box>

            <List component="nav" aria-label="API documentation sections" dense>
              {Object.keys(docsSections).map((sectionKey) => (
                <div key={sectionKey}>
                  <ListItemButton
                    selected={section === sectionKey}
                    onClick={() => handleSectionChange(sectionKey)}
                    sx={{
                      fontWeight: section === sectionKey ? 'bold' : 'normal',
                      py: 1.5,
                      color: section === sectionKey ? 'primary.main' : 'text.primary',
                      bgcolor: section === sectionKey ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      }
                    }}
                  >
                    <ListItemText
                      primary={docsSections[sectionKey].title} 
                    />
                  </ListItemButton>

                  {section === sectionKey && (
                    <List component="div" disablePadding>
                      {Object.keys(docsSections[sectionKey].subsections)
                        .map((subsectionKey) => (
                          <ListItemButton
                            key={subsectionKey}
                            selected={subsection === subsectionKey}
                            onClick={() => handleSubsectionChange(subsectionKey)}
                            sx={{
                              pl: 4,
                              py: 0.75,
                              color: subsection === subsectionKey ? 'primary.main' : 'text.secondary',
                              borderLeft: subsection === subsectionKey ? 3 : 0,
                              borderLeftColor: 'primary.main',
                              bgcolor: subsection === subsectionKey ? 'action.selected' : 'transparent',
                              '&:hover': {
                                bgcolor: 'action.hover',
                              }
                            }}
                          >
                            {subsection === subsectionKey ?
                              <KeyboardArrowRightIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> :
                              <Box sx={{ width: 24, ml: 0.5, mr: 1 }} />
                            }
                            <ListItemText
                              primary={docsSections[sectionKey].subsections[subsectionKey].title} 
                            />
                          </ListItemButton>
                        ))}
                    </List>
                  )}
                 { <Divider sx={{ my: 0.5 }} />}
                </div>
              ))}
            </List>
          </Paper>

          {/* Main Content Area */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              width: { xs: '100%', md: '900px' },
              flexShrink: 0
            }}
          >
            <Typography variant="h4" gutterBottom>
              {currentSubsection?.title || "Documentation"}
            </Typography>
            <Divider sx={{ my: 0.5, marginBottom: 2 }} />
            {/* Render the selected component */}
            <CurrentComponent />
          </Paper>
        </Stack>
      </Box>
    </Container>
  );
}


